<template>
  <div class="content-main relative">
    <BaseOrderModal
      @closeOrder="openedOrder = ''"
      @showDraft="showDraft"
      :orderUUID="openedOrder"
      @openParentOrder="openOrder"
      @openChildOrder="openOrder"
      @nextOrder="openNextOrder"
      @previousOrder="openPreviousOrder"
      v-model="showOrderModal"
      @orderUpdated="() => loadOrders(true)"
      :hasNext="hasNext"
      :hasPrev="hasPrev"
    />

    <!-- Mobile Menu Modal -->
    <div
      v-if="showMobileMenu"
      class="mobile-menu-overlay"
      @click="showMobileMenu = false"
    >
      <div class="mobile-menu-content" @click.stop>
        <div class="mobile-menu-header">
          <h3>{{ $t("message.orders_page.filter_by_status") }}</h3>
          <button @click="showMobileMenu = false" class="close-btn">
            <i class="mdi mdi-close"></i>
          </button>
        </div>
        <BaseLeftPageSubMenu
          :menuItems="orderStatusOptions"
          :selectedStatus="filters.statusShipment"
          :hideToggle="true"
          :searchEnabled="false"
          @selectedStatusFilter="updateStatusFilterMobile"
        >
        </BaseLeftPageSubMenu>
      </div>
    </div>

    <!-- Desktop Sidebar -->
    <div class="desktop-sidebar hidden lg:block">
      <BaseLeftPageSubMenu
        :menuItems="orderStatusOptions"
        :selectedStatus="filters.statusShipment"
        :hideToggle="true"
        :searchEnabled="false"
        @selectedStatusFilter="updateStatusFilter"
      >
      </BaseLeftPageSubMenu>
    </div>

    <template>
      <div class="content full-width">
        <div class="old-version-banner">
          <i class="mdi mdi-alert-circle-outline"></i>
          {{ $t("message.orders_page.old_version_warning") }}
        </div>
        <!-- Search Bar -->
        <div class="search-bar">
          <div class="search-wrapper">
            <i class="mdi mdi-magnify search-icon"></i>
            <input
              type="text"
              v-model="filters.search"
              :placeholder="$t('message.orders_page.search_placeholder')"
              class="search-input"
              @keyup.enter="applyFilters"
            />
            <button
              v-if="filters.search"
              @click="filters.search = ''"
              class="clear-search"
            >
              <i class="mdi mdi-close"></i>
            </button>
          </div>
          <div class="search-wrapper">
            <i class="mdi mdi-barcode search-icon"></i>
            <input
              type="text"
              v-model="filters.otherFilters.nkrCode"
              :placeholder="
                $t('message.orders_page.search_nkr_code_placeholder')
              "
              class="search-input"
              @keyup.enter="applyFilters"
            />
            <button
              v-if="filters.otherFilters.nkrCode"
              @click="filters.otherFilters.nkrCode = ''"
              class="clear-search"
            >
              <i class="mdi mdi-close"></i>
            </button>
          </div>
          <div class="search-actions">
            <button
              @click="applyFilters"
              class="btn-dark-button"
              :disabled="!hasFilterChanges"
            >
              {{ $t("message.orders_page.apply") }}
            </button>
            <button
              @click="resetFilters"
              class="btn-clear-button ml-2"
              v-if="hasFilterChanges"
            >
              {{ $t("message.orders_page.reset") }}
            </button>
          </div>
        </div>

        <div class="header flex flex-row">
          <BaseMiniSpinner v-if="loading" class="mr-2" />
          <div class="header-title">
            {{ $t("message.orders_page.title") }}
            {{
              filters.statusShipment
                ? orderStatusOptions.find(
                    option => option.value === filters.statusShipment
                  )?.originalDisplayText || ""
                : "(" +
                  $t("message.orders_page.order_statuses.all_status") +
                  ")"
            }}
          </div>
          <div class="header-actions">
            <div class="mobile-actions">
              <!-- Mobile menu button -->
              <button
                @click="showMobileMenu = true"
                class="mobile-menu-btn lg:hidden"
                :title="$t('message.orders_page.menu')"
              >
                <i class="mdi mdi-menu text-xl"></i>
              </button>
              <!-- Additional filters button -->
              <button
                @click="showFilters = true"
                class="filter-btn"
                :title="$t('message.orders_page.additional_filters')"
              >
                <i class="mdi mdi-filter-variant text-xl"></i>
              </button>
            </div>
          </div>
        </div>
        <OrdersFilters
          v-if="showFilters"
          :showFilters="showFilters"
          @closed="showFilters = false"
          v-model="filters.otherFilters"
          @clearFilters="clearFilters"
          @apply="
            applyFilters();
            showFilters = false;
          "
          :key="filtersKey"
        ></OrdersFilters>
        <!-- Active filter badges -->
        <div
          v-if="!showFilters && activeFilterBadges.length"
          class="active-filters-bar"
        >
          <span
            v-for="badge in activeFilterBadges"
            :key="badge.key"
            class="filter-badge"
          >
            {{ badge.label }}
            <button
              class="filter-badge-remove"
              @click="removeFilter(badge.key)"
            >
              <i class="mdi mdi-close"></i>
            </button>
          </span>
        </div>

        <div v-if="filters.statusShipment === 'dropship'">
          <!-- buttons for processed and not processed -->
          <button
            class="btn-info-button text-black"
            :class="{
              'border-4 border-red-500':
                filters.otherFilters.dropshipProcessed == 0
            }"
            @click="filters.otherFilters.dropshipProcessed = 0"
          >
            {{ $t("message.orders_page.not_processed") }}
          </button>
          <button
            class="btn-dark-button bg-green-500 text-black"
            :class="{
              'border-4 border-red-500':
                filters.otherFilters.dropshipProcessed == 1
            }"
            @click="filters.otherFilters.dropshipProcessed = 1"
          >
            {{ $t("message.orders_page.processed") }}
          </button>
        </div>
        <!-- Desktop Table View -->
        <div class="table desktop-table">
          <table class="default-table">
            <thead>
              <tr class="table100-head">
                <th
                  v-for="headerItem in tableHeaders"
                  :key="headerItem.value"
                  @click="changeArrowDir(headerItem.value)"
                >
                  {{ headerItem.displayText }}
                  <span v-if="filters.orderCol == headerItem.value">
                    <img
                      :src="
                        require(`@/assets/icons/${arrowDirection}-arrow.svg`)
                      "
                    />
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="order in orders"
                :key="order.orderNumber"
                @click="openOrder(order.orderNumber)"
                :class="{
                  selected: selectedOrder == order.id,
                  processed: order.dropshipProcessed == 1
                }"
              >
                <td>
                  <div class="flex items-center gap-2">
                    <a>
                      {{ order.orderNumber }}
                      {{
                        order.parentOrderNumber &&
                        order.parentOrderNumber !== order.orderNumber
                          ? ` (${$t("message.orders_page.parent")} ${
                              order.parentOrderNumber
                            })`
                          : ""
                      }}
                    </a>
                    <div
                      v-if="order.statusPayment == 0"
                      class="payment-warning-badge"
                      :title="$t('message.orders_page.not_paid')"
                    >
                      <i class="mdi mdi-alert-circle"></i>
                    </div>
                  </div>
                </td>
                <td>
                  {{ order.orderDate | dateWithHours }}
                </td>
                <td>
                  {{ translateStatus(order.status) }}
                </td>
                <td>
                  <BaseTooltip
                    :text="order.products"
                    :disabled="!order.products || order.products == ''"
                  >
                    {{ order.products }}
                  </BaseTooltip>
                </td>
                <td>
                  <BaseTooltip
                    :text="order.shippingAddressName"
                    :disabled="
                      !order.shippingAddressName ||
                        order.shippingAddressName == ''
                    "
                  >
                    {{ order.shippingAddressName }}
                  </BaseTooltip>
                </td>
                <td>
                  {{ order.shippingAddressState }}
                </td>
                <td>
                  {{ order.shippingAddressCountry }}
                </td>
                <td>
                  {{ order.createdBy }}
                </td>
                <td>{{ order.total | currencyFormatted(true) }}</td>
                <td>
                  {{ order.shippingPreference }}
                </td>
                <td>
                  <div
                    v-if="order.vin !== '' && order.vin !== null"
                    class="w-5 h-5 bg-green-500 rounded-full border-2 border-black"
                  ></div>
                </td>
                <td>{{ order.nombreVendedor }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Mobile Card View -->
        <div class="mobile-cards">
          <div
            v-for="order in orders"
            :key="order.orderNumber"
            @click="openOrder(order.orderNumber)"
            class="order-card"
            :class="{
              selected: selectedOrder == order.id,
              warning: order.statusPayment == 0,
              processed: order.dropshipProcessed == 1
            }"
          >
            <div class="card-header">
              <div class="order-number">
                <i class="mdi mdi-receipt text-blue-600"></i>
                <span class="font-bold">
                  {{ order.orderNumber }}
                  <span
                    v-if="
                      order.parentOrderNumber &&
                        order.parentOrderNumber !== order.orderNumber
                    "
                    class="text-xs text-gray-500"
                  >
                    ({{ $t("message.orders_page.parent") }}:
                    {{ order.parentOrderNumber }})
                  </span>
                </span>
              </div>
              <div class="order-total">
                <span class="font-bold text-green-600"
                  >{{ currency }}{{ order.total }}</span
                >
              </div>
            </div>

            <div class="card-content">
              <div class="info-row">
                <i class="mdi mdi-calendar text-gray-500"></i>
                <span>{{ order.orderDate | dateWithHours }}</span>
              </div>

              <div class="info-row" v-if="order.status">
                <i class="mdi mdi-information text-gray-500"></i>
                <span
                  >{{ $t("message.orders_page.status") }}:
                  {{ translateStatus(order.status) }}</span
                >
              </div>

              <div class="info-row" v-if="order.products">
                <i class="mdi mdi-package-variant text-gray-500"></i>
                <span class="truncate">{{ order.products }}</span>
              </div>

              <div class="info-row" v-if="order.shippingAddressName">
                <i class="mdi mdi-account text-gray-500"></i>
                <span class="truncate">{{ order.shippingAddressName }}</span>
              </div>

              <div
                class="info-row"
                v-if="
                  order.shippingAddressState || order.shippingAddressCountry
                "
              >
                <i class="mdi mdi-map-marker text-gray-500"></i>
                <span
                  >{{ order.shippingAddressState }},
                  {{ order.shippingAddressCountry }}</span
                >
              </div>

              <div class="info-row" v-if="order.createdBy">
                <i class="mdi mdi-account-circle text-gray-500"></i>
                <span
                  >{{ $t("message.orders_page.created_by") }}:
                  {{ order.createdBy }}</span
                >
              </div>

              <div class="info-row" v-if="order.shippingPreference">
                <i class="mdi mdi-truck text-gray-500"></i>
                <span>{{ order.shippingPreference }}</span>
              </div>

              <div class="info-row" v-if="order.salesman">
                <i class="mdi mdi-account-tie text-gray-500"></i>
                <span
                  >{{ $t("message.orders_page.seller") }}:
                  {{ order.nombreVendedor }}</span
                >
              </div>
            </div>

            <div class="card-footer">
              <div class="status-indicators">
                <div
                  v-if="order.vin !== '' && order.vin !== null"
                  class="vin-indicator"
                  :title="$t('message.orders_page.has_vin')"
                >
                  <i class="mdi mdi-car text-green-600"></i>
                </div>
                <div
                  v-if="order.statusPayment == 0"
                  class="payment-warning"
                  :title="$t('message.orders_page.payment_issue')"
                >
                  <i class="mdi mdi-alert-circle text-orange-500"></i>
                </div>
                <div
                  v-if="order.dropshipProcessed == 1"
                  class="processed-indicator"
                  :title="$t('message.orders_page.processed')"
                >
                  <i class="mdi mdi-check-circle text-green-600"></i>
                </div>
              </div>
              <div class="card-action">
                <i class="mdi mdi-chevron-right text-gray-400"></i>
              </div>
            </div>
          </div>
        </div>
        <div class="footer">
          <div class="totals">
            {{ $t("message.orders_page.showing") }}
            {{ appliedFilters.limitPerPage * (appliedFilters.page - 1) }}
            {{ $t("message.orders_page.to") }} {{ toQuantity() }}
            {{ $t("message.orders_page.from") }} {{ totalItems }}
            {{ $t("message.orders_page.results") }}
          </div>
          <div class="pages">
            <select
              v-model="ordersByPage"
              @change="
                (appliedFilters.limitPerPage = ordersByPage),
                  (appliedFilters.page = 1),
                  (filters.limitPerPage = ordersByPage),
                  (filters.page = 1)
              "
            >
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="500">500</option>
            </select>
            {{ $t("message.orders_page.per_page") }}
          </div>
          <div class="pages">
            <div>
              <a
                :class="{ disabled: appliedFilters.page == 0 }"
                class="page-link"
                @click="previous()"
                >{{ $t("message.orders_page.previous") }}</a
              >
            </div>
            <div>
              {{ $t("message.orders_page.page") }} {{ appliedFilters.page }}
            </div>
            <div>
              <a
                :class="{
                  disabled: totalPages > appliedFilters.page
                }"
                class="page-link"
                @click="next()"
                >{{ $t("message.orders_page.next") }}</a
              >
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import {
  getOrders,
  createQueryString,
  getOrdersQtyByStatus
} from "@/api/store/OrdersService.js";
import OrdersFilters from "@/components/order/OrdersFilters.vue";
import { orderStatusMap, getOrderStatusKey } from "@/constants/orderStatusMap";
const baseFilters = {
  statusShipment: "awaiting_shipment",
  otherFilters: {
    createdBy: "",
    seller: "",
    fromDate: "",
    toDate: "",
    paidStatus: "",
    creditRetail: "",
    hideCancelled: true,
    dropshipProcessed: 0,
    customerInfoautoId: "",
    nkrCode: "",
    shippingAddressName: "",
    totalMin: "",
    totalMax: ""
  },
  page: 1,
  limitPerPage: 20,
  orderCol: "orderDate",
  orderDir: "desc",
  search: ""
};
export default {
  name: "OrdersPage",
  components: { OrdersFilters },

  data() {
    return {
      publicPath: process.env.VUE_APP_IMG_BASE_URL,
      filters: JSON.parse(JSON.stringify(baseFilters)),
      appliedFilters: JSON.parse(JSON.stringify(baseFilters)),
      filtersKey: 0,
      ordersByPage: 20,
      orders: [],
      totalItems: 0,
      totalPages: 0,
      loading: true,
      selectedOrder: null,
      openedOrder: "",
      showOrderModal: false,
      showFilters: false,
      showMobileMenu: false,
      apiCallId: null,
      hasNext: false,
      hasPrev: false,
      windowIsFocused: true,
      generalStatus: null
    };
  },
  watch: {
    appliedFilters: {
      deep: true,
      handler() {
        this.loading = true;
        const query = {
          ...this.$route.query,
          search: createQueryString(this.appliedFilters)
        };
        this.$router.replace({ query }).catch(() => {});
        this.loadOrders();
      }
    },
    showFilters() {
      const query = {
        ...this.$route.query,
        showFilters: this.showFilters
      };
      this.$router.replace({ query }).catch(() => {});
    }
  },
  computed: {
    getAdminSettings() {
      return this.$store.getters["general/getAdminSettings"];
    },
    currency() {
      const currencyObj = this.getAdminSettings.get("currency");
      return currencyObj?.value || "$";
    },
    arrowDirection() {
      return this.filters.orderDir == "desc" ? "down" : "up";
    },
    hasFilterChanges() {
      return (
        JSON.stringify(this.filters) !== JSON.stringify(this.appliedFilters)
      );
    },
    activeFilterBadges() {
      const f = this.appliedFilters?.otherFilters;
      if (!f) return [];
      const badges = [];
      const paidLabels = { "1": "Paid", "0": "Pending payment" };
      const creditLabels = { "0": "Credit", "1": "Retail" };
      const createdByLabels = { user: "User", seller: "Admin-Seller" };
      if (f.customerInfoautoId)
        badges.push({
          key: "customerInfoautoId",
          label: `Customer: ${f.customerInfoautoId}`
        });
      if (f.seller)
        badges.push({ key: "seller", label: `Seller: ${f.seller}` });
      if (f.createdBy)
        badges.push({
          key: "createdBy",
          label: `Created by: ${createdByLabels[f.createdBy] || f.createdBy}`
        });
      if (f.fromDate)
        badges.push({ key: "fromDate", label: `From: ${f.fromDate}` });
      if (f.toDate) badges.push({ key: "toDate", label: `To: ${f.toDate}` });
      if (
        f.paidStatus !== "" &&
        f.paidStatus !== null &&
        f.paidStatus !== undefined
      )
        badges.push({
          key: "paidStatus",
          label: `Payment: ${paidLabels[f.paidStatus] || f.paidStatus}`
        });
      if (
        f.creditRetail !== "" &&
        f.creditRetail !== null &&
        f.creditRetail !== undefined
      )
        badges.push({
          key: "creditRetail",
          label: `Type: ${creditLabels[f.creditRetail] || f.creditRetail}`
        });
      if (f.shippingAddressName)
        badges.push({
          key: "shippingAddressName",
          label: `Ship name: ${f.shippingAddressName}`
        });
      if (f.totalMin)
        badges.push({ key: "totalMin", label: `Min $${f.totalMin}` });
      if (f.totalMax)
        badges.push({ key: "totalMax", label: `Max $${f.totalMax}` });
      if (f.nkrCode)
        badges.push({ key: "nkrCode", label: `Product: ${f.nkrCode}` });
      return badges;
    },
    tableHeaders() {
      return [
        {
          displayText: this.$t(
            "message.orders_page.table_headers.order_number"
          ),
          value: "orderNumber"
        },
        {
          displayText: this.$t("message.orders_page.table_headers.order_date"),
          value: "orderDate"
        },
        {
          displayText: this.$t("message.orders_page.table_headers.status"),
          value: "status"
        },
        {
          displayText: this.$t("message.orders_page.table_headers.products"),
          value: "products"
        },
        {
          displayText: this.$t(
            "message.orders_page.table_headers.shipping_name"
          ),
          value: "shippingName"
        },
        {
          displayText: this.$t("message.orders_page.table_headers.state"),
          value: "state"
        },
        {
          displayText: this.$t("message.orders_page.table_headers.country"),
          value: "country"
        },
        {
          displayText: this.$t("message.orders_page.table_headers.created_by"),
          value: "createdBy"
        },
        {
          displayText: this.$t("message.orders_page.table_headers.total"),
          value: "total"
        },
        {
          displayText: this.$t(
            "message.orders_page.table_headers.shipping_preference"
          ),
          value: "shippingPreference"
        },
        {
          displayText: this.$t("message.orders_page.table_headers.vin"),
          value: "vin"
        },
        {
          displayText: this.$t("message.orders_page.table_headers.seller"),
          value: "marketplace"
        }
      ];
    },
    orderStatusOptions() {
      return [
        {
          displayText: this.$t(
            "message.orders_page.order_statuses.awaiting_shipment"
          ),
          originalDisplayText: this.$t(
            "message.orders_page.order_statuses.awaiting_shipment"
          ),
          count: this.getStatusCount("awaiting_shipment"),
          value: "awaiting_shipment"
        },
        {
          displayText: this.$t(
            "message.orders_page.order_statuses.awaiting_payment"
          ),
          originalDisplayText: this.$t(
            "message.orders_page.order_statuses.awaiting_payment"
          ),
          count: this.getStatusCount("awaiting_payment"),
          value: "awaiting_payment"
        },
        {
          displayText: this.$t("message.orders_page.order_statuses.shipped"),
          originalDisplayText: this.$t(
            "message.orders_page.order_statuses.shipped"
          ),
          count: this.getStatusCount("shipped"),
          value: "shipped"
        },
        {
          displayText: this.$t(
            "message.orders_page.order_statuses.cancel_request_pending"
          ),
          originalDisplayText: this.$t(
            "message.orders_page.order_statuses.cancel_request_pending"
          ),
          count: this.getStatusCount("cancel_request_pending"),
          value: "Cancel request pending"
        },
        {
          displayText: this.$t("message.orders_page.order_statuses.cancelled"),
          originalDisplayText: this.$t(
            "message.orders_page.order_statuses.cancelled"
          ),
          count: this.getStatusCount("cancelled"),
          value: "cancelled"
        },
        {
          displayText: this.$t("message.orders_page.order_statuses.on_hold"),
          originalDisplayText: this.$t(
            "message.orders_page.order_statuses.on_hold"
          ),
          count: this.getStatusCount("on_hold"),
          value: "on_hold"
        },
        {
          displayText: this.$t(
            "message.orders_page.order_statuses.local_dropship"
          ),
          originalDisplayText: this.$t(
            "message.orders_page.order_statuses.local_dropship"
          ),
          count: this.getStatusCount("local_dropship"),
          value: "Local Dropship"
        },
        {
          displayText: this.$t(
            "message.orders_page.order_statuses.dropship_pending"
          ),
          originalDisplayText: this.$t(
            "message.orders_page.order_statuses.dropship_pending"
          ),
          count: this.getStatusCount("dropship_pending"),
          value: "Dropship Pending"
        },
        {
          displayText: this.$t(
            "message.orders_page.order_statuses.dropship_awaiting_shipment"
          ),
          originalDisplayText: this.$t(
            "message.orders_page.order_statuses.dropship_awaiting_shipment"
          ),
          count: this.getStatusCount("dropship_processed"),
          value: "Dropship processed"
        },
        {
          displayText: this.$t(
            "message.orders_page.order_statuses.vin_verification"
          ),
          originalDisplayText: this.$t(
            "message.orders_page.order_statuses.vin_verification"
          ),
          count: this.getStatusCount("vin_verification"),
          value: "VIN_verification"
        },
        {
          displayText: this.$t("message.orders_page.order_statuses.processing"),
          originalDisplayText: this.$t(
            "message.orders_page.order_statuses.processing"
          ),
          count: this.getStatusCount("processing"),
          value: "processing"
        },
        {
          displayText: this.$t("message.orders_page.order_statuses.all_status"),
          originalDisplayText: this.$t(
            "message.orders_page.order_statuses.all_status"
          ),
          value: ""
        },
        {
          displayText: this.$t("message.orders_page.order_statuses.draft"),
          originalDisplayText: this.$t(
            "message.orders_page.order_statuses.draft"
          ),
          count: this.getStatusCount("draft"),
          value: "draft"
        },
        {
          displayText: this.$t(
            "message.orders_page.order_statuses.warehouse_allocation"
          ),
          originalDisplayText: this.$t(
            "message.orders_page.order_statuses.warehouse_allocation"
          ),
          count: this.getStatusCount("warehouse_allocation"),
          value: "warehouse_allocation"
        }
      ];
    }
  },
  mounted() {
    if (this.$route.query.search) {
      let searchParams = new URLSearchParams(this.$route.query.search);
      for (let p of searchParams) {
        switch (p[1]) {
          case "true":
            p[1] = true;
            break;
          case "false":
            p[1] = false;
            break;
          case "null":
            p[1] = null;
            break;
          default:
            break;
        }
        if (p[0] === "otherFilters") {
          let otherFilters = p[1].split("&");
          otherFilters.forEach(element => {
            let filter = element.split("=");
            this.filters.otherFilters[filter[0]] = decodeURIComponent(
              filter[1]
            );
            console.log(decodeURIComponent(filter[1]));
          });
        } else {
          const decodedValue = decodeURIComponent(p[1]);
          this.filters[p[0]] =
            p[0] === "statusShipment"
              ? this.normalizeStatusShipment(decodedValue)
              : decodedValue;
        }
      }
      // Copy to applied filters
      this.appliedFilters = JSON.parse(JSON.stringify(this.filters));
    }
    if (this.$route.query.orderOpen) {
      this.openOrder(this.$route.query.orderOpen);
    }
    if (this.$route.query.showFilters) {
      this.showFilters = this.$route.query.showFilters == "true";
    }
    this.loadOrders();
    this.detectFocusOut();
    this.getStatus();
  },
  methods: {
    getStatusCount(statusValue) {
      if (!this.generalStatus) {
        return null;
      }

      const statusKeyMap = this.getStatusKeyMapping();
      const apiStatusKey = Object.keys(statusKeyMap).find(key => {
        return statusKeyMap[key] === statusValue;
      });

      if (!apiStatusKey || this.generalStatus[apiStatusKey] === undefined) {
        return null;
      }

      return this.generalStatus[apiStatusKey];
    },
    normalizeStatusShipment(value) {
      if (value === null || value === undefined) {
        return value;
      }

      const normalizedValue = `${value}`.replace(/_/g, " ").trim();
      const matchedOption = this.orderStatusOptions.find(option => {
        return (
          `${option.value}`.toLowerCase() === normalizedValue.toLowerCase()
        );
      });

      return matchedOption ? matchedOption.value : value;
    },
    translateStatus(status) {
      if (!status) return "";
      const key = getOrderStatusKey(status);
      if (key) {
        const translationKey = `message.orders_page.order_statuses.${key}`;
        const translated = this.$t(translationKey);
        return translated !== translationKey ? translated : status;
      }
      return status;
    },
    getStatusKeyMapping() {
      return orderStatusMap;
    },
    getStatus() {
      getOrdersQtyByStatus().then(res => {
        this.generalStatus = res;
      });
    },
    showDraft() {
      this.openedOrder = null;
      this.showOrderModal = false;
      const newFilters = JSON.parse(JSON.stringify(baseFilters));
      this.$nextTick(() => {
        newFilters.statusShipment = "draft";
        this.filters = newFilters;
      });
    },
    onWindowFocusChange(e) {
      console.log("window focus event");
      if ({ focus: 1, pageshow: 1 }[e.type]) {
        if (this.windowIsFocused) return;
        this.windowIsFocused = true;
        this.loadOrders(true);
        this.getStatus();
      } else if (this.windowIsFocused) {
        this.windowIsFocused = false;
      }
    },
    detectFocusOut() {
      this.windowIsFocused = false;
      window.addEventListener("focus", this.onWindowFocusChange);
      window.addEventListener("blur", this.onWindowFocusChange);
      window.addEventListener("pageshow", this.onWindowFocusChange);
      window.addEventListener("pagehide", this.onWindowFocusChange);
    },
    applyFilters() {
      this.appliedFilters = JSON.parse(JSON.stringify(this.filters));
    },
    resetFilters() {
      this.filters = JSON.parse(JSON.stringify(this.appliedFilters));
    },
    clearFilters() {
      this.filters.otherFilters = JSON.parse(
        JSON.stringify(baseFilters.otherFilters)
      );
      this.filtersKey++;
    },
    removeFilter(key) {
      const defaults = baseFilters.otherFilters;
      this.filters.otherFilters[key] = key in defaults ? defaults[key] : "";
      this.filtersKey++;
      this.applyFilters();
    },
    updateSearchFilter(search) {
      this.filters.page = 1;
      this.filters.search = search;
    },
    updateStatusFilter(status) {
      this.filters.page = 1;
      this.filters.statusShipment = status;
      if (status == "cancelled") {
        this.filters.otherFilters.hideCancelled = false;
      }
      this.showFilters = false;
      // Apply status filter changes immediately
      this.applyFilters();
    },
    updateStatusFilterMobile(status) {
      this.updateStatusFilter(status);
      this.showMobileMenu = false; // Close mobile menu after selection
    },
    async loadOrders(force = false) {
      this.apiCallId = new Date();
      let apiCallIdCurrent = this.apiCallId;
      const res = await getOrders(this.appliedFilters, {
        skipCache: force
      }).catch(() => {
        alert("Error getting orders");
      });
      if (apiCallIdCurrent !== this.apiCallId) {
        console.warn("new request in process");
        return;
      }
      this.orders = res.orders;
      this.totalItems = res.totalItems;
      this.totalItems = res.totalItems;
      this.totalPages = res.totalPages;
      this.loading = false;
    },
    previous() {
      if (this.appliedFilters.page > 1) {
        this.appliedFilters.page = this.appliedFilters.page - 1;
        this.filters.page = this.appliedFilters.page;
      }
    },
    next() {
      if (
        this.totalItems >=
        this.appliedFilters.limitPerPage * this.appliedFilters.page
      ) {
        this.appliedFilters.page = this.appliedFilters.page + 1;
        this.filters.page = this.appliedFilters.page;
      }
    },
    toQuantity() {
      if (this.qtyPerPage * this.appliedFilters.page > this.totalItems) {
        return this.orders.length;
      } else {
        return (
          this.appliedFilters.limitPerPage * (this.appliedFilters.page - 1) +
          this.orders.length
        );
      }
    },
    changeArrowDir(headerValue) {
      this.filters.page = 1;
      this.appliedFilters.page = 1;
      if (headerValue == this.filters.orderCol) {
        this.filters.orderDir =
          this.filters.orderDir == "desc" ? "asc" : "desc";
      } else {
        this.filters.orderCol = headerValue;
        this.filters.orderDir = "desc";
      }
      // Apply sorting changes immediately
      this.applyFilters();
    },
    openOrder(orderNumber) {
      this.openedOrder = orderNumber;
      this.showOrderModal = true;
      const index = this.orders.findIndex(
        order => order.orderNumber == this.openedOrder
      );
      if (index + 1 > this.orders.length - 1) {
        console.log("no tengo siguiente");
        this.hasNext = false;
      } else {
        console.log("xsi");
        this.hasNext = true;
      }

      // to do! = tengo previo?
      if (index - 1 < 0) {
        console.log("no tengo anterior");
        this.hasPrev = false;
      } else {
        console.log("si tengo anterior");
        this.hasPrev = true;
      }
    },
    openNextOrder() {
      const index = this.orders.findIndex(
        order => order.id == this.openedOrder
      );
      if (index + 1 > this.orders.length - 1) return;
      this.openOrder(this.orders[index + 1].orderNumber);
    },
    openPreviousOrder() {
      const index = this.orders.findIndex(
        order => order.id == this.openedOrder
      );
      if (index - 1 < 0) return;

      this.openOrder(this.orders[index - 1].orderNumber);
    }
  },
  beforeDestroy() {
    console.log("remove event listeners");
    window.removeEventListener("focus", this.onWindowFocusChange);
    window.removeEventListener("blur", this.onWindowFocusChange);
    window.removeEventListener("pageshow", this.onWindowFocusChange);
    window.removeEventListener("pagehide", this.onWindowFocusChange);
  }
};
</script>
<style lang="scss" scoped>
@import "@/assets/style/css/generic-variables.scss";

// Mobile menu modal
.mobile-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 60;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;

  @media (min-width: 1024px) {
    display: none;
  }

  .mobile-menu-content {
    background: white;
    width: 280px;
    max-width: 85vw;
    height: 100vh;
    overflow-y: auto;
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);

    .mobile-menu-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 20px;
      border-bottom: 1px solid #e5e7eb;
      background: #f9fafb;

      h3 {
        font-size: 18px;
        font-weight: 600;
        color: #111827;
        margin: 0;
      }

      .close-btn {
        padding: 4px;
        border: none;
        background: none;
        cursor: pointer;
        border-radius: 4px;
        transition: background-color 0.2s;

        &:hover {
          background: #e5e7eb;
        }

        i {
          font-size: 20px;
          color: #6b7280;
        }
      }
    }

    .left-menu {
      display: block !important;
      width: 100%;
      min-width: auto;
      box-shadow: none;
    }
  }
}

// Desktop sidebar
.desktop-sidebar {
  position: absolute;
  top: 0;
  left: 0;
  width: 250px;
  height: 100vh;
  background: white;
  border-right: 1px solid #e5e7eb;
  overflow-y: auto;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.05);

  @media (max-width: 1023px) {
    display: none !important;
  }
}

.content-main {
  position: relative;
  min-height: 100vh;
  overflow-x: hidden;

  @media (min-width: 1024px) {
    display: flex;

    .content {
      flex: 1;
      margin-left: 250px;
      max-width: calc(100vw - 250px);
    }
  }

  .content {
    padding: 5px;
    width: 100%;
    max-width: 100vw;

    &.full-width {
      max-width: 100%;
    }

    .search-bar {
      background: white;
      padding: 15px;
      border-bottom: 1px solid #e5e7eb;
      display: flex;
      gap: 12px;
      align-items: center;
      flex-wrap: wrap;

      @media (max-width: 768px) {
        padding: 10px;
        flex-direction: column;
        align-items: stretch;
      }

      .search-wrapper {
        position: relative;
        flex: 1;
        min-width: 300px;

        @media (max-width: 768px) {
          min-width: 100%;
        }

        .search-icon {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #6b7280;
          font-size: 20px;
          pointer-events: none;
          z-index: 1;
        }

        .search-input {
          width: 100%;
          padding: 10px 44px 10px 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 15px;
          transition: border-color 0.2s, box-shadow 0.2s;

          @media (max-width: 768px) {
            font-size: 16px;
            padding: 12px 44px 12px 12px;
          }

          &:focus {
            outline: none;
            border-color: $link-color-1;
            box-shadow: 0 0 0 3px rgba(18, 110, 106, 0.1);
          }

          &::placeholder {
            color: #9ca3af;
          }
        }

        .clear-search {
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
          background: transparent;
          border: none;
          padding: 4px;
          cursor: pointer;
          color: #6b7280;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          transition: background-color 0.2s;

          &:hover {
            background-color: #f3f4f6;
          }

          i {
            font-size: 18px;
          }
        }
      }

      .search-actions {
        display: flex;
        gap: 8px;

        @media (max-width: 768px) {
          width: 100%;
          justify-content: flex-end;
        }
      }
    }

    .header {
      width: 100%;
      padding: 20px 15px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 1px solid #e5e7eb;

      @media (max-width: 767px) {
        padding: 12px 8px;
        flex-wrap: wrap;
        gap: 12px;
      }

      .header-title {
        font-size: 20px;
        font-weight: bold;
        flex-shrink: 0;

        @media (max-width: 767px) {
          font-size: 18px;
          order: 1;
          width: 100%;
        }
      }

      .header-actions {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-shrink: 0;

        @media (max-width: 767px) {
          order: 2;
          width: 100%;
          justify-content: space-between;
        }

        .mobile-actions {
          display: flex;
          align-items: center;
          gap: 12px;

          .mobile-menu-btn,
          .filter-btn {
            background: none;
            border: 1px solid #d1d5db;
            cursor: pointer;
            padding: 8px;
            border-radius: 6px;
            transition: background-color 0.2s;
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 44px;
            min-height: 44px;

            &:hover {
              background: rgba(0, 0, 0, 0.05);
            }

            i {
              color: #374151;
            }
          }

          .filter-btn {
            @media (min-width: 768px) {
              // Show on desktop too for additional filters
            }
          }
        }

        .new-order-btn {
          white-space: nowrap;

          @media (max-width: 767px) {
            font-size: 14px;
            padding: 8px 16px;
          }
        }
      }
    }

    // Desktop Table
    .desktop-table {
      width: 100%;
      max-width: 100%;
      height: calc(100% - 200px);
      overflow-x: auto;
      margin-bottom: 10px;

      table {
        width: 100%;
        table-layout: fixed;

        th,
        td {
          font-size: 14px;
          padding: 8px 6px;
          overflow: hidden;
          text-overflow: ellipsis;
          text-align: left;
        }

        // Column widths - adjust based on content importance (12 columns)
        th:nth-child(1),
        td:nth-child(1) {
          width: 10%;
        } // Order Number
        th:nth-child(2),
        td:nth-child(2) {
          width: 10%;
        } // Date
        th:nth-child(3),
        td:nth-child(3) {
          width: 8%;
        } // Status
        th:nth-child(4),
        td:nth-child(4) {
          width: 10%;
        } // Products
        th:nth-child(5),
        td:nth-child(5) {
          width: 10%;
        } // Shipping Name
        th:nth-child(6),
        td:nth-child(6) {
          width: 7%;
        } // State
        th:nth-child(7),
        td:nth-child(7) {
          width: 7%;
        } // Country
        th:nth-child(8),
        td:nth-child(8) {
          width: 8%;
        } // Created By
        th:nth-child(9),
        td:nth-child(9) {
          width: 7%;
        } // Total
        th:nth-child(10),
        td:nth-child(10) {
          width: 9%;
        } // Shipping Pref
        th:nth-child(11),
        td:nth-child(11) {
          width: 4%;
        } // VIN
        th:nth-child(12),
        td:nth-child(12) {
          width: 10%;
        } // Seller

        // Hide Created By (8) and VIN (11) on tablet
        @media (max-width: 1023px) {
          th:nth-child(8),
          td:nth-child(8),
          th:nth-child(11),
          td:nth-child(11) {
            display: none;
          }

          // Redistribute widths on tablet (10 columns visible)
          th:nth-child(1),
          td:nth-child(1) {
            width: 12%;
          } // Order Number
          th:nth-child(2),
          td:nth-child(2) {
            width: 11%;
          } // Date
          th:nth-child(3),
          td:nth-child(3) {
            width: 9%;
          } // Status
          th:nth-child(4),
          td:nth-child(4) {
            width: 12%;
          } // Products
          th:nth-child(5),
          td:nth-child(5) {
            width: 12%;
          } // Shipping Name
          th:nth-child(6),
          td:nth-child(6) {
            width: 8%;
          } // State
          th:nth-child(7),
          td:nth-child(7) {
            width: 8%;
          } // Country
          th:nth-child(9),
          td:nth-child(9) {
            width: 8%;
          } // Total
          th:nth-child(10),
          td:nth-child(10) {
            width: 10%;
          } // Shipping Pref
          th:nth-child(12),
          td:nth-child(12) {
            width: 10%;
          } // Seller
        }
      }

      @media (max-width: 767px) {
        display: none !important;
      }
    }

    // Force hide table on mobile
    .table {
      @media (max-width: 767px) {
        display: none !important;
      }
    }

    // Mobile Cards
    .mobile-cards {
      display: none;

      @media (max-width: 767px) {
        display: block;
        padding: 8px 0;
      }

      @media (max-width: 390px) {
        padding: 4px 0;
      }

      .order-card {
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        margin-bottom: 12px;
        padding: 16px;
        cursor: pointer;
        transition: all 0.2s ease;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

        &:hover {
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
          transform: translateY(-1px);
        }

        &.selected {
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
        }

        &.warning {
          border-left: 4px solid #f59e0b;
          background-color: #fffbeb;
        }

        &.processed {
          border-left: 4px solid #10b981;
          background-color: #f0fdf4;
        }

        @media (max-width: 390px) {
          margin-bottom: 8px;
          padding: 12px;
          border-radius: 6px;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 12px;
          padding-bottom: 8px;
          border-bottom: 1px solid #f3f4f6;

          .order-number {
            display: flex;
            align-items: center;
            gap: 8px;
            flex: 1;

            i {
              font-size: 18px;
              flex-shrink: 0;
            }

            span {
              font-size: 16px;
              line-height: 1.4;
            }

            @media (max-width: 390px) {
              gap: 6px;

              i {
                font-size: 16px;
              }

              span {
                font-size: 14px;
              }
            }
          }

          .order-total {
            font-size: 16px;
            font-weight: bold;

            @media (max-width: 390px) {
              font-size: 14px;
            }
          }
        }

        .card-content {
          .info-row {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 8px;
            font-size: 14px;
            color: #374151;

            i {
              font-size: 16px;
              width: 16px;
              flex-shrink: 0;
            }

            span {
              flex: 1;
              min-width: 0;

              &.truncate {
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
            }

            @media (max-width: 390px) {
              gap: 6px;
              margin-bottom: 6px;
              font-size: 13px;

              i {
                font-size: 14px;
                width: 14px;
              }
            }
          }
        }

        .card-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 12px;
          padding-top: 8px;
          border-top: 1px solid #f3f4f6;

          .status-indicators {
            display: flex;
            gap: 8px;

            .vin-indicator,
            .payment-warning,
            .processed-indicator {
              i {
                font-size: 16px;
              }
            }

            @media (max-width: 390px) {
              gap: 6px;

              .vin-indicator,
              .payment-warning,
              .processed-indicator {
                i {
                  font-size: 14px;
                }
              }
            }
          }

          .card-action {
            i {
              font-size: 20px;
              transition: transform 0.2s ease;
            }
          }
        }

        &:hover .card-footer .card-action i {
          transform: translateX(2px);
        }
      }
    }

    .footer {
      display: flex;
      flex-direction: row;
      width: 100%;
      justify-content: space-between;

      @media (max-width: 767px) {
        flex-direction: column;
        gap: 12px;
        padding: 8px 0;
      }

      @media (max-width: 390px) {
        gap: 8px;
        font-size: 13px;
      }

      .pages {
        display: flex;
        flex-direction: row;

        div {
          width: 75px;

          @media (max-width: 390px) {
            width: auto;
            min-width: 60px;
          }
        }

        .page-link {
          color: $link-color-1;
        }

        @media (max-width: 767px) {
          justify-content: center;
          gap: 8px;
        }
      }

      .totals {
        @media (max-width: 767px) {
          text-align: center;
        }
      }
    }
  }

  // Payment warning badge
  .payment-warning-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background-color: #f59e0b;
    border-radius: 50%;
    color: white;
    flex-shrink: 0;
    animation: pulse-warning 2s ease-in-out infinite;

    i {
      font-size: 16px;
      line-height: 1;
    }
  }

  @keyframes pulse-warning {
    0%,
    100% {
      box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.7);
    }
    50% {
      box-shadow: 0 0 0 6px rgba(245, 158, 11, 0);
    }
  }

  // Responsive adjustments for filters and status buttons
  @media (max-width: 767px) {
    .btn-info-button,
    .btn-dark-button {
      font-size: 14px;
      padding: 8px 12px;
      margin: 4px;
    }
  }

  @media (max-width: 390px) {
    .btn-info-button,
    .btn-dark-button {
      font-size: 12px;
      padding: 6px 10px;
      margin: 2px;
    }
  }
}
.old-version-banner {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: #fef3c7;
  border-bottom: 2px solid #f59e0b;
  color: #92400e;
  font-size: 13px;
  font-weight: 600;

  i {
    vertical-align: middle;
  }
}

.active-filters-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 6px 12px;
}

.filter-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: #e8f0fe;
  color: #1a56db;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

.filter-badge-remove {
  display: inline-flex;
  align-items: center;
  color: #1a56db;
  opacity: 0.7;
  line-height: 1;

  &:hover {
    opacity: 1;
  }
}
</style>
